import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Upload, FileText, Sparkles, Plus, Trash2, Save, User, Briefcase,
  GraduationCap, Zap, Globe, Award, Heart, Loader2, CheckCircle2
} from "lucide-react";

export default function EmployeeCVPage() {
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { setCv((await api.get("/cv/me")).data.cv); } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/cv/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCv(r.data.cv);
      toast.success("CV estratto con AI! Rivedi e personalizza i campi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally { setUploading(false); }
  };

  const save = async (partial) => {
    setSaving(true);
    try {
      const r = await api.patch("/cv/me", partial);
      setCv(r.data.cv);
    } catch (e) { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const update = (patch) => setCv({ ...cv, ...patch });
  const updatePersonal = (patch) => setCv({ ...cv, personal: { ...(cv.personal || {}), ...patch } });

  if (loading) return <div className="p-6 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline"/> Caricamento…</div>;

  // ---- UPLOAD SCREEN ----
  if (!cv) {
    return (
      <div className="p-5 md:p-8 max-w-[720px] mx-auto" data-testid="cv-upload-screen">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] font-bold tracking-[0.18em] uppercase mb-5 bg-indigo-100 text-indigo-700">
            <Sparkles className="w-3 h-3" /> AI CV Builder
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Il tuo CV, generato in 30 secondi</h1>
          <p className="mt-3 text-slate-600 max-w-[480px] mx-auto">
            Carica il tuo CV in PDF, DOCX o TXT. L'AI (GPT-5) estrarrà automaticamente tutti i campi.
            Potrai poi personalizzarli come preferisci.
          </p>
        </div>

        <label
          data-testid="cv-dropzone"
          className="block border-2 border-dashed rounded-3xl p-10 text-center cursor-pointer transition-all hover:bg-slate-50"
          style={{ borderColor: uploading ? "#6366F1" : "#CBD5E1", background: uploading ? "#EEF2FF" : "#FFFFFF" }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.md"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="cv-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="w-14 h-14 mx-auto text-indigo-500 animate-spin mb-4" />
              <div className="text-lg font-bold text-slate-900">AI in azione…</div>
              <div className="text-sm text-slate-500 mt-1">Sto estraendo nome, esperienze, skills, lingue, formazione…</div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                <Upload className="w-8 h-8" />
              </div>
              <div className="text-lg font-bold text-slate-900">Trascina qui il tuo CV o clicca per selezionare</div>
              <div className="text-sm text-slate-500 mt-2">PDF · DOCX · TXT · max 10 MB</div>
            </>
          )}
        </label>

        <div className="mt-6 text-center">
          <button
            onClick={() => save({ summary: "" })}
            className="text-sm text-slate-500 hover:text-slate-700 underline"
            data-testid="cv-manual-start"
          >
            Oppure crea il CV manualmente da zero
          </button>
        </div>
      </div>
    );
  }

  // ---- EDITABLE CV FORM ----
  const personal = cv.personal || {};
  return (
    <div className="p-5 md:p-8 max-w-[920px] mx-auto space-y-5" data-testid="cv-edit-screen">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-indigo-600 mb-1">— Il mio CV</div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Curriculum Vitae</h1>
          <p className="text-sm text-slate-500 mt-1">Aggiornato il {cv.updated_at ? new Date(cv.updated_at).toLocaleString("it-IT") : "—"}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="cv-reupload-btn" className="rounded-xl">
            <Upload className="w-4 h-4 mr-1"/> Rigenera da file
          </Button>
          <Button onClick={() => save(cv)} disabled={saving} data-testid="cv-save-btn" className="rounded-xl bg-indigo-500 hover:bg-indigo-600">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Save className="w-4 h-4 mr-1"/>} Salva
          </Button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt,.md" className="hidden" onChange={(e)=>handleFile(e.target.files?.[0])}/>

      {/* Personal */}
      <Section icon={User} title="Dati personali" color="#3B82F6">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Nome completo" value={personal.full_name} onChange={(v)=>updatePersonal({ full_name: v })} testid="cv-fullname"/>
          <Field label="Email" value={personal.email} onChange={(v)=>updatePersonal({ email: v })} testid="cv-email"/>
          <Field label="Telefono" value={personal.phone} onChange={(v)=>updatePersonal({ phone: v })} testid="cv-phone"/>
          <Field label="Data di nascita" value={personal.birth_date} onChange={(v)=>updatePersonal({ birth_date: v })} placeholder="YYYY-MM-DD" testid="cv-birth"/>
          <Field label="Città" value={personal.city} onChange={(v)=>updatePersonal({ city: v })} testid="cv-city"/>
          <Field label="Nazionalità" value={personal.nationality} onChange={(v)=>updatePersonal({ nationality: v })} testid="cv-nat"/>
          <Field label="LinkedIn" value={personal.linkedin || ""} onChange={(v)=>updatePersonal({ linkedin: v })} testid="cv-linkedin" className="md:col-span-2"/>
        </div>
      </Section>

      <Section icon={Sparkles} title="Riassunto professionale" color="#7C3AED">
        <textarea value={cv.summary || ""} onChange={e=>update({ summary: e.target.value })} data-testid="cv-summary"
          rows={4} maxLength={1000}
          placeholder="Breve descrizione del profilo professionale, obiettivi, punti di forza…"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none"/>
      </Section>

      <ArraySection title="Esperienza professionale" icon={Briefcase} color="#06B6D4" testid="cv-experience"
        items={cv.experience || []} onChange={(exp)=>update({ experience: exp })}
        fields={[
          { k: "role", label: "Ruolo", span: 2 },
          { k: "company", label: "Azienda", span: 2 },
          { k: "from", label: "Dal (YYYY-MM)" },
          { k: "to", label: "Al (YYYY-MM)" },
          { k: "description", label: "Descrizione", span: 4, textarea: true },
        ]}
      />

      <ArraySection title="Formazione" icon={GraduationCap} color="#8B5CF6" testid="cv-education"
        items={cv.education || []} onChange={(edu)=>update({ education: edu })}
        fields={[
          { k: "degree", label: "Titolo", span: 2 },
          { k: "field", label: "Ambito", span: 2 },
          { k: "institution", label: "Istituto", span: 4 },
          { k: "from", label: "Dal" },
          { k: "to", label: "Al" },
        ]}
      />

      <ChipSection title="Competenze (skills)" icon={Zap} color="#F59E0B" testid="cv-skills"
        items={cv.skills || []} onChange={(s)=>update({ skills: s })} placeholder="Aggiungi skill (es. Excel, Python)"/>

      <ArraySection title="Lingue" icon={Globe} color="#10B981" testid="cv-languages"
        items={cv.languages || []} onChange={(l)=>update({ languages: l })}
        fields={[
          { k: "name", label: "Lingua", span: 2 },
          { k: "level", label: "Livello (A1-C2 o madrelingua)", span: 2 },
        ]}
      />

      <ChipSection title="Certificazioni" icon={Award} color="#EC4899" testid="cv-certs"
        items={cv.certifications || []} onChange={(c)=>update({ certifications: c })} placeholder="Aggiungi certificazione"/>

      <ChipSection title="Interessi" icon={Heart} color="#F472B6" testid="cv-interests"
        items={cv.interests || []} onChange={(i)=>update({ interests: i })} placeholder="Aggiungi interesse"/>

      <div className="sticky bottom-4 flex justify-center">
        <Button onClick={() => save(cv)} disabled={saving} data-testid="cv-save-sticky"
          className="rounded-full px-8 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-xl shadow-indigo-500/30">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Salvataggio…</> : <><CheckCircle2 className="w-4 h-4 mr-2"/> Salva tutto</>}
        </Button>
      </div>
    </div>
  );
}

// ---------- helpers ----------

function Section({ icon: Icon, title, color, children }) {
  return (
    <section className="rounded-2xl p-5 md:p-6 bg-white border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)` }}>
          <Icon className="w-4 h-4"/>
        </div>
        <h3 className="text-base font-bold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, className = "", testid }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold tracking-wide uppercase text-slate-500 mb-1.5">{label}</label>
      <input value={value || ""} onChange={e=>onChange(e.target.value)} placeholder={placeholder || ""} data-testid={testid}
        className="w-full px-3 h-10 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-indigo-400"/>
    </div>
  );
}

function ArraySection({ title, icon, color, items, onChange, fields, testid }) {
  const add = () => onChange([...items, Object.fromEntries(fields.map(f => [f.k, ""]))]);
  const upd = (i, patch) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <Section icon={icon} title={title} color={color}>
      <div className="space-y-3" data-testid={testid}>
        {items.map((it, i) => (
          <div key={i} className="rounded-xl p-4 border border-slate-200 bg-slate-50/50 relative">
            <button onClick={()=>del(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" aria-label="Rimuovi">
              <Trash2 className="w-4 h-4"/>
            </button>
            <div className="grid md:grid-cols-4 gap-3">
              {fields.map(f => (
                <div key={f.k} className={f.span === 4 ? "md:col-span-4" : f.span === 2 ? "md:col-span-2" : ""}>
                  <label className="block text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1">{f.label}</label>
                  {f.textarea ? (
                    <textarea value={it[f.k] || ""} onChange={e=>upd(i, { [f.k]: e.target.value })} rows={2}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none"/>
                  ) : (
                    <input value={it[f.k] || ""} onChange={e=>upd(i, { [f.k]: e.target.value })}
                      className="w-full px-3 h-9 rounded-xl border border-slate-200 text-sm"/>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <Button onClick={add} variant="outline" className="rounded-xl w-full" data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4 mr-1"/> Aggiungi
        </Button>
      </div>
    </Section>
  );
}

function ChipSection({ title, icon, color, items, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...items, v]);
    setInput("");
  };
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <Section icon={icon} title={title} color={color}>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={testid}>
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold"
            style={{ background: `${color}15`, color }}>
            {it}
            <button onClick={()=>del(i)} className="hover:opacity-70"><Trash2 className="w-3 h-3"/></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-slate-400">Nessuno ancora</span>}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();add();}}}
          placeholder={placeholder} data-testid={`${testid}-input`}
          className="flex-1 px-3 h-10 rounded-xl border border-slate-200 text-sm"/>
        <Button onClick={add} data-testid={`${testid}-add`} className="rounded-xl" style={{ background: color, color: "#FFFFFF" }}>
          <Plus className="w-4 h-4"/>
        </Button>
      </div>
    </Section>
  );
}
